import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import { V1Scheduler } from 'takeorder-groupbuying-scheduler-js';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { mapState } from 'vuex';

import { SCHEDULE_TYPES, WEEKDAYS } from '@/utils/constants';
import { getScheduleDates } from '@/utils/cycleOrder';

export default {
  name: 'ScheduleModal',
  props: {
    autoResetVuelidate: {
      default: true,
      type: Boolean,
    },
    departmentIds: {
      default: () => [],
      type: Array,
    },
    receiveDateStart: {
      default: null,
      type: Date,
    },
    schedule: {
      default: () => ({}),
      type: Object,
    },
    value: {
      default: false,
      type: Boolean,
    },
    viewMode: {
      default: false,
      type: Boolean,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      datumSchedule: {},
      dayInterval: 1,
      loading: false,
      orderCreateDay: 0,
      orderCreateTime: null, // 記錄時間，日期不重要
      orderPeriodDay: 0,
      orderPeriodTime: {}, // 記錄時間，日期不重要
      orderStart: 0,
      receiveDateEnd: null,
      scheduleType: SCHEDULE_TYPES.PERIOD,
      weekdayIntervals: [],
    };
  },
  computed: {
    ...mapState('orders', ['departments']),
    currentDepartmentIds() {
      const { departmentIds: scheduleDepartmentIds = [] } = this.schedule || {};
      return scheduleDepartmentIds?.length ? scheduleDepartmentIds : this.departmentIds || [];
    },
    dayIntervalOptions() {
      return Array.from(
        { length: 30 },
        (_, i) => ({ label: `${i + 1} 天`, value: i + 1 }),
      );
    },
    departmentNameMapping() {
      return this.departments?.reduce((map, { id, name }) => {
        // eslint-disable-next-line no-param-reassign
        map[id] = name;
        return map;
      }, {}) || {};
    },
    departmentText() {
      return this.currentDepartmentIds
        .map((id) => this.departmentNameMapping[id])
        .filter(Boolean)
        .join('、');
    },
    isConfirmBtnDisable() {
      if (isEmpty(this.datumSchedule)) {
        return this.loading || this.v$.$invalid;
      }
      return this.loading || !this.v$.$dirty || this.v$.$invalid;
    },
    isDayType() {
      return this.scheduleType === SCHEDULE_TYPES.PERIOD;
    },
    isWeekType() {
      return this.scheduleType === SCHEDULE_TYPES.WEEKLY;
    },
    orderCreateDayOptions() {
      return Array.from(
        { length: 8 },
        (_, i) => ({ label: i ? `前 ${i} 天` : '當下', value: i }),
      );
    },
    orderPeriodDayOptions() {
      const length = this.orderStart;
      if (!length) {
        return [{ label: 'ㄧ', value: 0 }];
      }
      return Array.from(
        { length },
        (_, i) => ({ label: `${i + 1} 天`, value: i + 1 }),
      );
    },
    orderPeriodTimeRange() {
      if (this.orderPeriodDay === 1) {
        return {
          end: {
            max: dayjs(this.orderPeriodTime.end).endOf('day').toDate(),
            min: this.orderPeriodTime.start,
          },
          start: {
            max: this.orderPeriodTime.end,
            min: dayjs(this.orderPeriodTime.start).startOf('day').toDate(),
          },
        };
      }

      return {
        end: {
          max: dayjs(this.orderPeriodTime.end).endOf('day').toDate(),
          min: dayjs(this.orderPeriodTime.end).startOf('day').toDate(),
        },
        start: {
          max: dayjs(this.orderPeriodTime.start).endOf('day').toDate(),
          min: dayjs(this.orderPeriodTime.start).startOf('day').toDate(),
        },
      };
    },
    orderStartOptions() {
      return Array.from(
        { length: 31 },
        (_, i) => ({ label: !i ? '當天' : `${i} 天前`, value: i }),
      );
    },
    receiveDateEndSelectableDates() {
      const start = this.receiveDateStart ? dayjs(this.receiveDateStart) : dayjs();
      const dates = getScheduleDates({
        dayInterval: this.dayInterval,
        startDate: start,
        type: this.scheduleType,
        weekdayIntervals: this.weekdayIntervals,
      });
      if (!dates.length) {
        return [];
      }

      const theLastDate = dates[dates.length - 1];
      const diff = theLastDate.diff(start, 'day');
      return Array.from(
        { length: diff + 1 },
        ((_, i) => start.add(i, 'day').toDate()),
      ).filter((date) => !dayjs(date).isAfter(dayjs('2025/1/8'), 'day'));
    },
    receiveDateStartText() {
      if (!this.receiveDateStart) {
        return '';
      }
      const start = dayjs(this.receiveDateStart);
      return `${start.format('YYYY/MM/DD')} ${WEEKDAYS[start.day()]}`;
    },
    SCHEDULE_TYPES() {
      return SCHEDULE_TYPES;
    },
    schedules() {
      const config = {
        type: this.scheduleType,
        period: this.dayInterval,
        weekday: [...this.weekdayIntervals].sort(),
        orderStart: this.orderStart,
        orderPeriodDay: this.orderPeriodDay,
        orderPeriodTime: [
          dayjs(this.orderPeriodTime.start).format('HH:mm'),
          dayjs(this.orderPeriodTime.end).format('HH:mm'),
        ],
        orderCreateDay: this.orderCreateDay,
        orderCreateTime: dayjs(this.orderCreateTime).format('HH:mm'),
        lastReceiveDate: dayjs(this.receiveDateEnd).format('YYYY-MM-DD'),
        groupBuyingStartTime: this.receiveDateStart ? dayjs(this.receiveDateStart).format() : null,
        groupBuyingReceiveTimes: [dayjs().format(), dayjs().format()],
      };

      const schedules = V1Scheduler.getAllSchedule(config);
      return schedules;
    },
    weekdayOptions() {
      return Array.from(
        { length: WEEKDAYS.length },
        (_, i) => {
          const key = (i + 1) % 7;
          return { label: WEEKDAYS[key], value: key };
        },
      );
    },
  },
  mounted() {
    this.datumSchedule = cloneDeep(this.schedule);
    this.setValues(this.schedule);
  },
  watch: {
    orderPeriodTime: {
      deep: true,
      handler(newVal) {
        const { start } = newVal || {};
        if (!start) {
          return;
        }
        const time = dayjs(start);
        this.orderCreateTime = dayjs()
          .hour(time.hour())
          .minute(time.minute())
          .toDate();
      },
    },
    orderStart(newVal) {
      if (this.orderPeriodDay > newVal || (this.orderPeriodDay === 0 && newVal > 0)) {
        this.orderPeriodDay = newVal;
      }
    },
    schedule(newVal) {
      this.datumSchedule = cloneDeep(newVal);
      this.setValues(newVal);
    },
    scheduleType() {
      if (!this.v$.$dirty) {
        this.v$.$reset();
      }
      if (this.isWeekType) {
        this.v$.weekdayIntervals.$touch();
      }
      if (!isEmpty(this.datumSchedule)) {
        this.v$.$touch();
      }
    },
    value(newVal) {
      if (newVal) {
        this.reset();
      }
    },
  },
  methods: {
    close() {
      this.reset();
      this.$emit('close');
    },
    formatDate(date) {
      return dayjs(date).format('YYYY/MM/DD');
    },
    formatTime(time) {
      return dayjs(time).format('A hh:mm');
    },
    onConfirm() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        return;
      }
      const data = {
        departmentIds: this.departmentIds,
        orderCreateDay: this.orderCreateDay,
        orderCreateTime: this.orderCreateTime,
        orderPeriodDay: this.orderPeriodDay,
        orderPeriodTime: this.orderPeriodTime,
        orderStart: this.orderStart,
        receiveDateEnd: this.receiveDateEnd,
        receiveDateStart: this.receiveDateStart,
        type: this.scheduleType,
      };
      if (this.isDayType) {
        data.dayInterval = this.dayInterval;
      } else {
        data.weekdayIntervals = [...this.weekdayIntervals].sort();
      }
      this.$emit('confirm', data);
      this.close();
    },
    reset() {
      if (this.autoResetVuelidate) {
        this.v$.$reset();
      }
      this.setValues(this.datumSchedule);
    },
    setValues(val = {}) {
      const createOrderPeriodTime = (source, receiveDateStart) => {
        const result = {
          end: source.end,
          start: source.start,
        };
        if (!result.end) {
          const now = dayjs();
          const receiveStart = dayjs(receiveDateStart);
          result.end = now
            .set('hour', receiveStart.hour())
            .set('minute', receiveStart.minute())
            .toDate();
        }
        if (!result.start) {
          const now = dayjs();
          const receiveStart = dayjs(receiveDateStart);
          const offsetDate = now
            .set('hour', receiveStart.hour())
            .set('minute', receiveStart.minute())
            .subtract(2, 'hour');
          result.start = offsetDate.isBefore(now, 'day')
            ? now.startOf('day').toDate() : now
              .set('hour', offsetDate.hour())
              .set('minute', offsetDate.minute())
              .toDate();
        }
        return result;
      };

      const createReceiveDateEnd = (
        source, dayInterval, receiveDateStart, scheduleType, weekdayIntervals,
      ) => {
        if (source) {
          return source;
        }
        const scheduleDates = getScheduleDates({
          dayInterval,
          startDate: receiveDateStart,
          type: scheduleType,
          weekdayIntervals,
        });

        const theLastDate = scheduleDates?.[scheduleDates.length - 1]?.toDate();
        return theLastDate || new Date();
      };

      const {
        dayInterval,
        orderCreateDay,
        orderCreateTime,
        orderPeriodDay,
        orderPeriodTime = {},
        orderStart,
        receiveDateEnd,
        type,
        weekdayIntervals = [],
      } = val || {};

      this.dayInterval = dayInterval || 1;
      this.orderPeriodDay = orderPeriodDay || 0;
      this.orderPeriodTime = createOrderPeriodTime(orderPeriodTime, this.receiveDateStart);
      this.orderStart = orderStart || 0;
      this.orderCreateDay = orderCreateDay || 0;
      this.orderCreateTime = orderCreateTime || dayjs(this.orderPeriodTime.start).toDate();
      this.scheduleType = type || SCHEDULE_TYPES.PERIOD;
      this.weekdayIntervals = weekdayIntervals || [];
      this.receiveDateEnd = createReceiveDateEnd(
        receiveDateEnd,
        this.dayInterval,
        this.receiveDateStart,
        this.scheduleType,
        this.weekdayIntervals,
      );
    },
  },
  validations() {
    return {
      orderPeriodTime: {
        end: {
          beforeReceiveDateStart: helpers.withMessage('不得晚於起始領取時間', () => {
            if (this.orderStart > 0) {
              return true;
            }

            const receiveStart = dayjs(this.receiveDateStart);
            const orderEnd = dayjs(this.orderPeriodTime.end);
            const offsetEnd = receiveStart.clone().set('hour', orderEnd.hour()).set('minute', orderEnd.minute());
            return !offsetEnd.isAfter(receiveStart, 'minute');
          }),
          notBeforeOrderPeriodTimeStart: helpers.withMessage('訖日結束時間不得早於起日時間', () => {
            if (this.orderPeriodDay > 1) {
              return true;
            }

            const start = dayjs(this.orderPeriodTime.start);
            const end = dayjs(this.orderPeriodTime.end);

            return !end.isBefore(start);
          }),
        },
        start: {
          beforeReceiveDateStart: helpers.withMessage('不得晚於起始領取時間', () => {
            if (this.orderStart > 0) {
              return true;
            }

            const receiveStart = dayjs(this.receiveDateStart);
            const orderStart = dayjs(this.orderPeriodTime.start);
            const offsetStart = receiveStart.clone().set('hour', orderStart.hour()).set('minute', orderStart.minute());
            return !offsetStart.isAfter(receiveStart, 'minute');
          }),
          notAfterOrderPeriodTimeEnd: helpers.withMessage('起日開始時間不得晚於訖日時間', () => {
            if (this.orderPeriodDay > 1) {
              return true;
            }
            const start = dayjs(this.orderPeriodTime.start);
            const end = dayjs(this.orderPeriodTime.end);

            return !start.isAfter(end);
          }),
        },
      },
      weekdayIntervals: {
        isFilled: helpers.withMessage('請選擇至少一個星期', () => {
          if (this.isDayType) {
            return true;
          }
          return this.weekdayIntervals.length > 0;
        }),
      },
    };
  },
};
